// Anonymous, GDPR-safe landing-visit counter (no cookies, no PII, no IP storage).
//
// Captures referrer + UTM tags on first arrival, persists them in sessionStorage so
// the AuthPage can attach them to the signup payload — enabling proper channel
// attribution (YouTube/Google/AI search/Blog/Social/etc.).
//
// Fires once per browser session per page so the count reflects "unique visits", not page reloads.

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ATTRIBUTION_KEY = 'mdd_attribution_v1';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function readQueryUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    UTM_KEYS.forEach(k => {
      const v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
}

/**
 * Capture the visitor's attribution data ONCE per session (first visit wins),
 * so we don't overwrite a "YouTube" landing with a later "internal blog click".
 */
function captureAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const existing = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (existing) return JSON.parse(existing);
  } catch { /* ignore */ }

  const utm = readQueryUtm();
  const referrer = document.referrer || '';
  const attribution = {
    referrer,
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    landing_url: window.location.href,
    captured_at: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch { /* ignore */ }
  return attribution;
}

/** Read the captured attribution — used by AuthPage to attach to signup. */
export function getAttribution() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return captureAttribution();
}

export async function trackLandingVisit(page) {
  if (typeof window === 'undefined') return;
  const attribution = captureAttribution();
  const key = `mdd_visit_tracked_${page}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage disabled — still attempt to track once per pageload
  }
  try {
    await axios.post(`${API_URL}/api/track/visit`, {
      page,
      referrer: attribution ? attribution.referrer : '',
      utm: attribution ? {
        source: attribution.utm_source,
        medium: attribution.utm_medium,
        campaign: attribution.utm_campaign,
      } : {},
    });
  } catch {
    // silently ignore — we never want this to affect the user experience
  }
}
